import { z } from 'zod'
import { RepairEvidenceSchema } from '../guidance/journey-repair/citations'
import { Link } from './nav'
import { taskDetailUrl } from '../lib/taskDetailPath'

const Evidence = z.array(RepairEvidenceSchema)
const Decline = z.object({ decision: z.literal('decline'), reason: z.string(), evidence: Evidence })
function declineResult(reason: string | null) {
  if (!reason) return null
  try {
    const parsed = Decline.safeParse(JSON.parse(reason))
    return parsed.success ? parsed.data : null
  } catch { return null }
}
export function SupportingEvidence({ evidence }: { evidence: z.infer<typeof Evidence> }) {
  return <details><summary>Supporting evidence</summary><ul>
    {evidence.map((item, index) => <li key={index}>
      {'tool' in item ? <>
        <strong>{item.tool}</strong> — {item.finding}
        <details><summary>Arguments and recorded result</summary>
          <pre>{JSON.stringify(item.params, null, 2)}</pre><pre>{item.result}</pre>
        </details>
      </> : <><strong>{item.source}: {item.reference}</strong> — {item.finding}</>}
    </li>)}
  </ul></details>
}
const Summary = z.object({ id: z.string(), state: z.string(), reason: z.string().nullable(), source_run_id: z.string(),
  candidates: z.array(z.object({ id: z.string(), attempt: z.number(), state: z.string(), run_id: z.string(),
    proposal: z.object({ problem: z.string().optional(), reason: z.string(), changes: z.unknown(), evidence: Evidence.optional() }), review: z.object({ decision: z.string(), reason: z.string(), evidence: Evidence.optional() }).nullable(),
  })),
})
export default function SessionRepairSummary({ value, projectId }: { value: unknown; projectId: string }) {
  const parsed = Summary.safeParse(value)
  if (!parsed.success) return null
  const repair = parsed.data
  const decline = declineResult(repair.reason)
  return <section className="test-run-detail__followups">
    <h2>Journey repair: {repair.state}</h2>
    <p><Link to={taskDetailUrl(projectId, repair.source_run_id)}>Original journey run</Link></p>
    {repair.reason && <p>{decline?.reason ?? repair.reason}</p>}
    {decline && <SupportingEvidence evidence={decline.evidence} />}
    {repair.candidates.map(candidate => <div key={candidate.id}>
      <p><Link to={taskDetailUrl(projectId, candidate.run_id)}>Verification {candidate.attempt}</Link> — {candidate.state}</p>
      {candidate.proposal.problem && <p><strong>Problem:</strong> {candidate.proposal.problem}</p>}
      <p><strong>Repair:</strong> {candidate.proposal.reason}</p>
      {candidate.proposal.evidence && <SupportingEvidence evidence={candidate.proposal.evidence} />}
      {candidate.review?.evidence && <SupportingEvidence evidence={candidate.review.evidence} />}
      {candidate.review && <p>Review: {candidate.review.decision}. {candidate.review.reason}</p>}
      <details><summary>Proposed project changes</summary><pre>{JSON.stringify(candidate.proposal.changes, null, 2)}</pre></details>
    </div>)}
  </section>
}
